import { useCallback } from "react";

import { useTenant } from "@/contexts/tenant-provider";

import { resolveCalendarEventTitle } from "@/lib/event-title";

import { EventTitleFormat } from "@/types/enums/event-title-format";
import { EventTiny } from "@/types/event";

/**
 * Returns a resolver for an event's display title using the tenant's configured
 * `event_title_format`.
 *
 * Non-CUSTOM appointments persist a NULL title and render dynamically from the
 * patient + service(s); a manual override wins (COALESCE); blocks/shifts/gcal
 * keep their raw title. Use this anywhere a title is shown to staff or patients
 * — never read `event.title` directly, or non-CUSTOM appointments render blank.
 *
 * Pass `providerId` to scope the services to a single provider's calendar lane;
 * omit it for the combined title (tooltips, search, lists, detail).
 */
export function useEventTitle() {
  const { tenant } = useTenant();
  const format = tenant?.eventTitleFormat ?? EventTitleFormat.PATIENT_SERVICES;
  return useCallback(
    (event: EventTiny, providerId?: string) =>
      resolveCalendarEventTitle(event, format, providerId),
    [format],
  );
}
